import { isManagerRole } from "../helpers/roles";
import React, { useEffect, useMemo, useState } from "react";
import { Table, Spinner, Button, Form, Modal, Card, Nav } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import CopyOnHover from "../components/CopyOnHover";
import {formatDateToKyiv} from "../helpers/utils";
import NovaPayPaymentModal from "../components/NovaPayPaymentModal";

const STATUS_OPTIONS = ["новий", "річ повернено", "оплачено"];

function returnEventActionLabel(action) {
    switch (action) {
        case "patch":
            return "Редагування";
        case "bulk_status":
            return "Масовий статус";
        case "novapay_paid":
            return "NovaPay (оплачено)";
        case "deleted":
            return "Видалення заявки";
        case "bulk_deleted":
            return "Масове видалення";
        default:
            return action || "—";
    }
}

const STATUS_TABS = [
    { value: "всі", label: "Усі", path: "all" },
    { value: "новий", label: "Новий", path: "new" },
    { value: "річ повернено", label: "Річ повернено", path: "returned" },
    { value: "оплачено", label: "Оплачено", path: "paid" },
];

const STATUS_BY_TAB_PATH = Object.fromEntries(STATUS_TABS.map(({ value, path }) => [path, value]));
const TAB_PATH_BY_STATUS = Object.fromEntries(STATUS_TABS.map(({ value, path }) => [value, path]));

function getDefaultStatusFilter(user) {
    return user?.role === "director" || user?.role === "finance" || user?.role === "admin"
        ? "річ повернено"
        : "новий";
}

/** Додатковий ФОП у модалці редагування (не з довідника NovaPay) */
const EDIT_MODAL_EXTRA_FOP = {
    key: "plekhanov-oleksii",
    label: "ФОП Плеханов Олексій Миколайович",
};

function parseRefundUah(refundAmount) {
    const n = parseFloat(String(refundAmount ?? "").replace(/[^\d.-]+/g, ""));
    return Number.isFinite(n) ? n : NaN;
}

function parseBalanceUah(balanceVal) {
    if (balanceVal == null) return NaN;
    const n = typeof balanceVal === "number" ? balanceVal : parseFloat(String(balanceVal).replace(",", "."));
    return Number.isFinite(n) ? n : NaN;
}

/** Повна назва з NOVAPAY / балансів === значення `fop` у заявці */
function findNovapayBalanceRow(balances, canonicalFopName) {
    if (!canonicalFopName?.trim() || !Array.isArray(balances)) return null;
    const t = canonicalFopName.trim();
    return balances.find((b) => (b.name || "").trim() === t) ?? null;
}

/**
 * Ключ для злиття дублікатів (латинське «i» замість «і» в українських іменах тощо).
 */
function fopDedupeKey(s) {
    const raw = String(s || "")
        .trim()
        .normalize("NFC")
        .replace(/(?<=[\u0400-\u04FF])i(?=[\u0400-\u04FF])/gi, "і");
    return raw.toLowerCase();
}

/** Короткий підпис з повної офіційної назви (лише для старих заявок у БД) */
function shortFopNameFromCanonical(canonicalName) {
    if (!canonicalName?.trim()) return "";
    const s = canonicalName.trim();
    if (!s.toLowerCase().startsWith("фоп")) return s;
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts[1] === "Нежид" && parts[2] === "Ірина") return "ФОП Ірина";
    return parts.length >= 2 ? `ФОП ${parts[1]}` : s;
}

/**
 * Знайти рядок fopOptionItems за тим, що збережено в заявці:
 * повний label, displayLabel, або скорочення від повної офіційної назви (label).
 */
function findNovapayFopOption(fopOptionItems, storedFop) {
    const t = (storedFop || "").trim();
    if (!t || !Array.isArray(fopOptionItems)) return null;
    const exact = fopOptionItems.find((x) => x.label === t);
    if (exact) return exact;
    const byDisplay = fopOptionItems.find((x) => (x.displayLabel || "").trim() === t);
    if (byDisplay) return byDisplay;
    return fopOptionItems.find((x) => shortFopNameFromCanonical(x.label) === t) ?? null;
}

/**
 * Підпис ФОП у таблиці / балансах (як у користувача на повну):
 * 1) офіційна повна назва NovaPay `opt.label`, якщо заявку зіставлено з рахунком;
 * 2) інакше повний `directory.displayTitle` з GET /api/users, якщо скорочення з БД збігається зі скороченням від displayTitle;
 * 3) інакше сирий рядок з заявки.
 */
function resolveFopDisplayName(fopOptionItems, userDirectory, storedFop) {
    const t = (storedFop || "").trim();
    if (!t) return "";

    const opt = findNovapayFopOption(fopOptionItems, t);
    if (opt?.label) {
        return opt.label.trim();
    }

    if (Array.isArray(userDirectory)) {
        const dirUser = userDirectory.find((u) => {
            const dt = (u.directory?.displayTitle || "").trim();
            return dt && shortFopNameFromCanonical(dt) === t;
        });
        const title = (dirUser?.directory?.displayTitle || "").trim();
        if (title) return title;
    }

    return t;
}

const ReturnsList = ({ user }) => {
    const navigate = useNavigate();
    const { tab } = useParams();
    /** ФОП для UI: рахунки NovaPay + користувачі з префіксом ФОП у профілі (без дублікатів із Nova) */
    const [fopOptionItems, setFopOptionItems] = useState([]);
    /** Усі користувачі з GET /api/users (для resolveFopDisplayName по slug) */
    const [userDirectory, setUserDirectory] = useState([]);
    const [returns, setReturns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [crmLinks, setCrmLinks] = useState({});
    const [editModalData, setEditModalData] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [copiedWhich, setCopiedWhich] = useState(null); // null | "standard" | "taxFree"
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");
    const [novapayBalances, setNovapayBalances] = useState([]);
    const [paymentModalData, setPaymentModalData] = useState(null);
    const [paidReturns, setPaidReturns] = useState({});
    const [showAll, setShowAll] = useState(false);
    /** Одна модалка: журнал усіх подій по заявках (лише ADF) */
    const [globalHistoryModalOpen, setGlobalHistoryModalOpen] = useState(false);
    const [recentEventsLoading, setRecentEventsLoading] = useState(false);

    /** Об'єднаний журнал по заяві (API + legacy у документі) */
    const [editModalHistoryEvents, setEditModalHistoryEvents] = useState([]);
    const [editModalHistoryLoading, setEditModalHistoryLoading] = useState(false);
    /** Останні події по всіх заявках (включно з видаленнями) */
    const [recentReturnEvents, setRecentReturnEvents] = useState([]);
    const statusFilter = STATUS_BY_TAB_PATH[tab] || getDefaultStatusFilter(user);

    const loadRecentReturnEvents = (opts = {}) => {
        const { showLoading = false } = opts;
        if (showLoading) setRecentEventsLoading(true);
        return fetch("/api/returns/events/recent?limit=120", { credentials: "include" })
            .then((r) => (r.ok ? r.json() : { events: [] }))
            .then((d) => setRecentReturnEvents(Array.isArray(d.events) ? d.events : []))
            .catch(() => setRecentReturnEvents([]))
            .finally(() => {
                if (showLoading) setRecentEventsLoading(false);
            });
    };

    /** Модалка: усі ФОП з NovaPay + ФОП Плеханов (якщо ще немає в списку) */
    const fopModalSelectItems = useMemo(() => {
        const byLabel = new Map();
        for (const x of fopOptionItems) {
            byLabel.set(x.label, x);
        }
        if (!byLabel.has(EDIT_MODAL_EXTRA_FOP.label)) {
            byLabel.set(EDIT_MODAL_EXTRA_FOP.label, EDIT_MODAL_EXTRA_FOP);
        }
        return [...byLabel.values()].sort((a, b) => a.label.localeCompare(b.label, "uk"));
    }, [fopOptionItems]);

    useEffect(() => {
        const arr = (x) => (Array.isArray(x) ? x : []);

        Promise.all([
            fetch("/api/users", { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
            fetch("/api/users/novapay-fop-options", { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
        ])
            .then(([users, novaOptions]) => {
                const userList = arr(users);
                setUserDirectory(userList);

                /** Усі рахунки NovaPay; підпис з профілю /users за `fopSlug`, якщо користувач є */
                const canonical = arr(novaOptions);
                const userBySlug = Object.fromEntries(
                    userList
                        .filter((u) => String(u?.integrations?.fopSlug || "").trim())
                        .map((u) => [u.integrations.fopSlug.trim(), u])
                );

                const items = canonical
                    .filter((x) => x?.key && x?.label)
                    .map((x) => {
                        const u = userBySlug[x.key];
                        const fromProfile = (u?.directory?.displayTitle || "").trim();
                        const displayLabel = fromProfile || u?.username?.trim() || (x.displayLabel ?? x.label);
                        return {
                            key: x.key,
                            label: x.label,
                            displayLabel,
                        };
                    });

                /** Користувачі з непорожнім «префікс ФОП» у профілі — як у адмінці «Відображення» */
                const userFopRows = userList
                    .filter((u) => String(u?.profile?.fopTitlePrefix || "").trim())
                    .map((u) => {
                        const label = (u.directory?.displayTitle || "").trim();
                        if (!label) return null;
                        const slug = String(u?.integrations?.fopSlug || "").trim();
                        const key = slug ? `user-fop-${slug}` : `user-fop-${u._id}`;
                        return { key, label, displayLabel: label };
                    })
                    .filter(Boolean);

                const mergedList = [];
                const seenDedupe = new Set();
                for (const x of items) {
                    const dk = fopDedupeKey(x.displayLabel || x.label);
                    if (seenDedupe.has(dk)) continue;
                    seenDedupe.add(dk);
                    mergedList.push(x);
                }
                for (const row of userFopRows) {
                    if (findNovapayFopOption(items, row.label)) continue;
                    const dk = fopDedupeKey(row.label);
                    if (seenDedupe.has(dk)) continue;
                    seenDedupe.add(dk);
                    mergedList.push(row);
                }

                const mergedItems = mergedList.sort((a, b) =>
                    a.label.localeCompare(b.label, "uk")
                );

                setFopOptionItems(mergedItems);
            })
            .catch(() => {
                setFopOptionItems([]);
                setUserDirectory([]);
            });
    }, []);

    useEffect(() => {
        const fetchBalances = async () => {
            try {
                const res = await fetch("/api/novapay/balances");
                const data = await res.json();
                setNovapayBalances(data);
            } catch (e) {
                console.error("Не вдалося завантажити баланси NovaPay", e);
            }
        };

        fetchBalances();
    }, []);

    const REFUND_PURPOSE_STANDARD = "Повернення коштів";
    const REFUND_PURPOSE_TAX_FREE = "Повернення коштів, не підлягає оподаткуванню";

    const handleCopyPurpose = async (which, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedWhich(which);
            setTimeout(() => setCopiedWhich(null), 2000);
        } catch (err) {
            console.error("Не вдалося скопіювати текст:", err);
        }
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (tab && STATUS_BY_TAB_PATH[tab]) return;
        navigate(`/returns-list/${TAB_PATH_BY_STATUS[getDefaultStatusFilter(user)]}`, { replace: true });
    }, [navigate, tab, user?.role]);

    const fetchReturns = async () => {
        try {
            const url = showAll
                ? "/api/returns/all?all=true"
                : "/api/returns/all?days=30";

            const res = await fetch(url, { credentials: "include" });
            const data = await res.json();
            const filtered = statusFilter === "всі" ? data : data.filter((r) => r.status === statusFilter);
            setReturns(filtered);
            setIsLoading(false);

            loadRecentReturnEvents();

            const toCheck = filtered.filter((r) => ["новий", "річ повернено"].includes(r.status));
            if (!toCheck.length) return;

            const today = new Date();
            const DAYS = 28;
            const fromDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - DAYS);
            const from = fromDate.toISOString().split("T")[0];
            const to = today.toISOString().split("T")[0];

            try {
                const summaryRes = await fetch(
                    `/api/summary?start=${encodeURIComponent(from)}&end=${encodeURIComponent(to)}&minimal=1`,
                    { credentials: "include" }
                );
                if (summaryRes.ok) {
                    const summaries = await summaryRes.json();
                    const allOrders = summaries.flatMap((s) => s.orders || []);
                    const crmLinkResults = {};

                    for (const r of toCheck) {
                        const number = r.orderNumber?.toString();
                        const isSiteOrder = number?.length === 4;
                        if (!number) continue;

                        let match;
                        if (isSiteOrder) {
                            match = allOrders.find((o) => o.source_uuid === number);
                        } else {
                            match = allOrders.find((o) => o.id?.toString() === number);
                        }
                        if (match?.id) {
                            crmLinkResults[number] = `https://lavastudio.keycrm.app/app/orders/view/${match.id}`;
                        }
                    }
                    setCrmLinks(crmLinkResults);
                }
            } catch (summaryErr) {
                console.warn("CRM-посилання: не вдалося завантажити summary", summaryErr);
            }
        } catch (err) {
            console.error("Помилка завантаження повернень:", err);
            setIsLoading(false);
        }
    };

    const isManager = isManagerRole(user?.role);
    const isADF = user?.role === "admin" || user?.role === "director" || user?.role === "finance";

    const filteredReturns = returns.filter((r) => {
        const term = searchTerm.toLowerCase();
        const fopShown = resolveFopDisplayName(fopOptionItems, userDirectory, r.fop).toLowerCase();
        return (
            r.name?.toLowerCase().includes(term) ||
            r.phone?.toLowerCase().includes(term) ||
            r.orderNumber?.toString().includes(term) ||
            r.fop?.toLowerCase().includes(term) ||
            fopShown.includes(term)
        );
    });

    const handleEdit = (r) => setEditModalData(r);

    const handleEditSave = async () => {
        try {
            const { _id, refundAmount, fop, status, orderNumber } = editModalData;

            const body = {
                refundAmount,
                fop,
                status,
                orderNumber,
                actorUsername: user?.username || user?.email || "",
            };

            const res = await fetch(`/api/returns/${_id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(body),
            });

            if (!res.ok) {
                let msg = "Не вдалося зберегти зміни";
                try {
                    const errBody = await res.json();
                    if (errBody?.error) msg = errBody.error;
                } catch {
                    /* ignore */
                }
                window.alert(msg);
                return;
            }

            setEditModalData(null);
            fetchReturns();
            loadRecentReturnEvents();
        } catch (err) {
            console.error("Помилка редагування:", err);
        }
    };


    const handleDelete = async () => {
        try {
            const res = await fetch(`/api/returns/${deletingId}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    actorUsername: user?.username || user?.email || "",
                }),
            });
            if (!res.ok) {
                let msg = "Не вдалося видалити заяву";
                try {
                    const errBody = await res.json();
                    if (errBody?.error) msg = errBody.error;
                } catch {
                    /* ignore */
                }
                window.alert(msg);
                return;
            }
            setReturns((prev) => prev.filter((r) => r._id !== deletingId));
            setShowConfirmModal(false);
            setDeletingId(null);
            loadRecentReturnEvents();
        } catch (err) {
            console.error("Помилка видалення:", err);
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const sortedReturns = [...filteredReturns].sort((a, b) => {
        if (!sortField) return 0;

        const sortValue = (row) => {
            if (sortField === "refundAmount") {
                return parseFloat(row.refundAmount.replace(/[^\d.-]+/g, ""));
            }
            if (sortField === "fop") {
                return resolveFopDisplayName(fopOptionItems, userDirectory, row.fop);
            }
            return row[sortField];
        };

        const valA = sortValue(a);
        const valB = sortValue(b);

        if (typeof valA === "string") {
            return sortOrder === "asc"
                ? valA.localeCompare(valB, "uk")
                : valB.localeCompare(valA, "uk");
        }

        if (typeof valA === "number") {
            return sortOrder === "asc" ? valA - valB : valB - valA;
        }

        return 0;
    });

    useEffect(() => {
        fetchReturns();
    }, [statusFilter, showAll]);

    useEffect(() => {
        loadRecentReturnEvents();
    }, []);

    useEffect(() => {
        if (!editModalData?._id) {
            setEditModalHistoryEvents([]);
            return;
        }
        let cancelled = false;
        setEditModalHistoryLoading(true);
        fetch(`/api/returns/${editModalData._id}/history`, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : { events: [] }))
            .then((data) => {
                if (!cancelled) {
                    setEditModalHistoryEvents(Array.isArray(data.events) ? data.events : []);
                }
            })
            .catch(() => {
                if (!cancelled) setEditModalHistoryEvents([]);
            })
            .finally(() => {
                if (!cancelled) setEditModalHistoryLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [editModalData?._id]);

    useEffect(() => {
        if (!globalHistoryModalOpen) return;
        loadRecentReturnEvents({ showLoading: true });
    }, [globalHistoryModalOpen]);

    const renderPaymentAction = (r) => {
        if (r.status !== "річ повернено" || !isADF) return null;
        if (paidReturns[r._id]) {
            return <Button variant="success" size="sm" disabled>Сплачено</Button>;
        }
        const fopOpt = findNovapayFopOption(fopOptionItems, r.fop);
        const canonicalFopForBalance = fopOpt?.label || r.fop;
        const balRow = findNovapayBalanceRow(novapayBalances, canonicalFopForBalance);
        if (!balRow) return null;
        if (balRow.error) {
            return (
                <div className="d-flex flex-column align-items-start gap-1" style={{ maxWidth: 220 }}>
                    <Button variant="warning" size="sm" disabled>
                        Повернути кошти
                    </Button>
                    <span className="small text-danger">Баланс NovaPay недоступний</span>
                </div>
            );
        }
        const refund = parseRefundUah(r.refundAmount);
        const balNum = parseBalanceUah(balRow.balance);
        if (!Number.isFinite(refund)) {
            return (
                <div className="d-flex flex-column align-items-start gap-1" style={{ maxWidth: 220 }}>
                    <Button variant="warning" size="sm" disabled title="Не вдалося прочитати суму повернення">
                        Повернути кошти
                    </Button>
                    <span className="small text-danger">Невірний формат суми</span>
                </div>
            );
        }
        if (!Number.isFinite(balNum)) {
            return (
                <div className="d-flex flex-column align-items-start gap-1" style={{ maxWidth: 220 }}>
                    <Button variant="warning" size="sm" disabled>
                        Повернути кошти
                    </Button>
                    <span className="small text-danger">Баланс недоступний</span>
                </div>
            );
        }
        const kopBal = Math.round(balNum * 100);
        const kopRef = Math.round(refund * 100);
        if (kopBal < kopRef) {
            return (
                <div className="d-flex flex-column align-items-start gap-1" style={{ maxWidth: 220 }}>
                    <Button
                        variant="warning"
                        size="sm"
                        disabled
                        title={`На рахунку ${balNum.toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн, потрібно ${refund.toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`}
                    >
                        Повернути кошти
                    </Button>
                    <span className="small text-danger">Недостатньо коштів на рахунку ФОП</span>
                </div>
            );
        }
        return (
            <Button variant="warning" size="sm" onClick={() => setPaymentModalData(r)}>
                Повернути кошти
            </Button>
        );
    };

    if (isLoading) return <Spinner animation="border" />;

    return (
        <div className="container-fluid mt-4">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 mb-lg-4">
                <h3 className="mb-0 me-auto">Заяви на повернення</h3>
                <div className="d-flex flex-wrap align-items-center gap-2 flex-md-nowrap">
                    {!isMobile && isADF && (
                        <Button
                            variant="outline-secondary"
                            onClick={() => setGlobalHistoryModalOpen(true)}
                            className="m-0 text-nowrap"
                        >
                            Історія змін
                        </Button>
                    )}
                    {!isMobile && isADF && (
                        <>
                            <Button
                                variant={copiedWhich === "standard" ? "success" : "outline-success"}
                                onClick={() => handleCopyPurpose("standard", REFUND_PURPOSE_STANDARD)}
                                className="m-0"
                                style={{ userSelect: "none", touchAction: "manipulation" }}
                            >
                                {copiedWhich === "standard" ? "✅ Скопійовано" : REFUND_PURPOSE_STANDARD}
                            </Button>
                            <Button
                                variant={copiedWhich === "taxFree" ? "success" : "outline-success"}
                                onClick={() => handleCopyPurpose("taxFree", REFUND_PURPOSE_TAX_FREE)}
                                className="m-0"
                                style={{ userSelect: "none", touchAction: "manipulation", whiteSpace: "normal" }}
                            >
                                {copiedWhich === "taxFree" ? "✅ Скопійовано" : REFUND_PURPOSE_TAX_FREE}
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outline-primary"
                        onClick={() => {
                            setShowAll((prev) => !prev);
                        }}
                    >
                        {showAll ? "Показати за 30 днів" : "Показати всі"}
                    </Button>
                    {user?.role === "admin" && (<Button variant="warning" onClick={() => {
                        fetch("/api/returns/test-generate")
                            .then(res => res.json())
                            .then(data => {
                                if (data.link) {
                                    window.open(data.link, "_blank");
                                } else {
                                    alert("Помилка при генерації тестового PDF");
                                }
                            });
                    }}>
                        🧪 Створити тестовий PDF
                    </Button>)}
                </div>
            </div>
            {isMobile && isADF && (
                <div className="mb-3">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setGlobalHistoryModalOpen(true)}
                        className="m-0"
                    >
                        Історія змін
                    </Button>
                </div>
            )}
            {isMobile && !isManager && (
                <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
                    <Button
                        variant={copiedWhich === "standard" ? "success" : "outline-success"}
                        onClick={() => handleCopyPurpose("standard", REFUND_PURPOSE_STANDARD)}
                        className="m-0"
                        style={{ userSelect: "none", touchAction: "manipulation" }}
                    >
                        {copiedWhich === "standard" ? "✅ Скопійовано" : REFUND_PURPOSE_STANDARD}
                    </Button>
                    <Button
                        variant={copiedWhich === "taxFree" ? "success" : "outline-success"}
                        onClick={() => handleCopyPurpose("taxFree", REFUND_PURPOSE_TAX_FREE)}
                        className="m-0"
                        style={{ userSelect: "none", touchAction: "manipulation", whiteSpace: "normal" }}
                    >
                        {copiedWhich === "taxFree" ? "✅ Скопійовано" : REFUND_PURPOSE_TAX_FREE}
                    </Button>
                </div>
            )}
            {isADF && novapayBalances.length > 0 && (
                <div className="mb-3 bg-light rounded px-3 py-2 border d-flex flex-wrap gap-3 align-items-center">
                    <strong>💰 Баланси NovaPay:</strong>
                    {novapayBalances.map((b, i) => (
                        <div key={i} className="d-flex align-items-center gap-2">
                            <span>{resolveFopDisplayName(fopOptionItems, userDirectory, b.name)}:</span>
                            {b.error ? (
                                <span className="text-danger">❌</span>
                            ) : (
                                <strong>{b.balance} грн</strong>
                            )}
                        </div>
                    ))}
                </div>
            )}
            <div className="mb-3 overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
                <Nav variant="tabs" className="flex-nowrap">
                    {STATUS_TABS.map(({ value, label, path }) => (
                        <Nav.Item key={value}>
                            <Nav.Link
                                active={statusFilter === value}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/returns-list/${path}`);
                                }}
                                href={`/returns-list/${path}`}
                                className="text-nowrap"
                            >
                                {label}
                            </Nav.Link>
                        </Nav.Item>
                    ))}
                </Nav>
            </div>
            <div className="position-relative mb-3" style={{ maxWidth: "100%" }}>
                <Form.Control
                    type="text"
                    placeholder="Пошук за ПІБ, телефоном або номером замовлення"
                    value={searchTerm}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            setSearchTerm("");
                        }
                    }}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ height: "50px", paddingRight: "2.5rem" }}
                />
                {searchTerm && (
                    <Button
                        variant="link"
                        onClick={() => setSearchTerm("")}
                        className="position-absolute end-0 top-50 translate-middle-y p-2 border-0 me-2"
                        style={{ fontSize: "1.2rem", textDecoration: "none", color: "#6c757d" }}
                    >
                        &times;
                    </Button>
                )}
            </div>
            {isMobile ? (
                <div className="d-flex flex-column gap-3">
                    {sortedReturns.map((r) => (
                        <Card key={r._id} className="shadow-sm p-3 position-relative">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="fw-bold">
                                    {crmLinks[r.orderNumber] ? (
                                        <a href={crmLinks[r.orderNumber]} target="_blank" rel="noopener noreferrer" style={{ color: "#0d6efd" }}>
                                            {r.orderNumber}
                                        </a>
                                    ) : (
                                        r.orderNumber
                                    )}
                                </div>
                                <div>
                                    <a href={r.driveLink} target="_blank" rel="noopener noreferrer">PDF</a>
                                </div>
                            </div>
                            {!isADF && <div>{r.name}</div>}
                            {!isADF && <div>{r.phone}</div>}
                            <div className="pt-2"><strong>{r.refundAmount.replace(" грн", "")} грн</strong></div>
                            {!isManager && <CopyOnHover text={r.iban?.replace(/\s+/g, "")} />}
                            {!isManager && <CopyOnHover text={r.cardOwnerTaxId} />}
                            {!isManager && <CopyOnHover text={r.cardOwner} />}
                            <div>{resolveFopDisplayName(fopOptionItems, userDirectory, r.fop)}</div>
                            <div className="pt-2" style={{ color: "#198754" }}>{r.status}</div>
                            {isADF && <div className="pt-2">{renderPaymentAction(r)}</div>}
                            {r.status === "оплачено" && r.paidAt && isManager && (
                                <div style={{ fontStyle: "italic", fontSize: "0.85rem" }}>
                                    Оплачено: {formatDateToKyiv(r.paidAt)}
                                </div>
                            )}
                            <div className="position-absolute" style={{ right: 10, bottom: 10 }}>
                                <div className="mt-2 d-inline-flex gap-1 align-items-center flex-nowrap justify-content-end">
                                    <Button
                                        variant="link"
                                        className="p-0 border-0 shadow-none text-decoration-none lh-1"
                                        style={{ fontSize: "1rem", minWidth: 0, color: "#fd7e14" }}
                                        onClick={() => handleEdit(r)}
                                        aria-label="Редагувати"
                                    >
                                        ✏️
                                    </Button>
                                    {user?.role === "admin" && (
                                        <Button
                                            variant="link"
                                            className="p-0 border-0 shadow-none text-decoration-none lh-1"
                                            style={{ fontSize: "1rem", minWidth: 0, color: "#dc3545" }}
                                            onClick={() => {
                                                setDeletingId(r._id);
                                                setShowConfirmModal(true);
                                            }}
                                            aria-label="Видалити"
                                        >
                                            🗑
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="table-holder">
                    <Table striped bordered hover responsive className="table-light table-sticky">
                        <thead>
                        <tr>
                            <th onClick={() => handleSort("orderNumber")} style={{ cursor: "pointer" }}>
                                Номер {sortField === "orderNumber" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            {!isADF && (
                                <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                                    ПІБ {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                                </th>
                            )}
                            {!isADF && (
                                <th onClick={() => handleSort("phone")} style={{ cursor: "pointer" }}>
                                    Телефон {sortField === "phone" && (sortOrder === "asc" ? "↑" : "↓")}
                                </th>
                            )}
                            <th onClick={() => handleSort("refundAmount")} style={{ cursor: "pointer" }}>
                                Сума {sortField === "refundAmount" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th onClick={() => handleSort("created_at")} style={{ cursor: "pointer" }}>
                                Дата {sortField === "created_at" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            {!isManager && (
                                <th onClick={() => handleSort("iban")} style={{ cursor: "pointer" }}>
                                    IBAN {sortField === "iban" && (sortOrder === "asc" ? "↑" : "↓")}
                                </th>
                            )}
                            {!isManager && (
                                <th onClick={() => handleSort("cardOwnerTaxId")} style={{ cursor: "pointer" }}>
                                    ІПН {sortField === "cardOwnerTaxId" && (sortOrder === "asc" ? "↑" : "↓")}
                                </th>
                            )}
                            {!isManager && (
                                <th onClick={() => handleSort("cardOwner")} style={{ cursor: "pointer" }}>
                                    ПІБ власника картки {sortField === "cardOwner" && (sortOrder === "asc" ? "↑" : "↓")}
                                </th>
                            )}
                            <th onClick={() => handleSort("fop")} style={{ cursor: "pointer" }}>
                                ФОП {sortField === "fop" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            {isADF && <th>Оплата</th>}
                            <th>Статус</th>
                            {isManager && <th>Дата оплати</th>}
                            <th>PDF</th>
                            <th>Дії</th>
                        </tr>
                        </thead>
                        <tbody>
                        {sortedReturns.map((r) => (
                            <tr key={r._id}>
                                <td>
                                    {crmLinks[r.orderNumber] ? (
                                        <a href={crmLinks[r.orderNumber]} target="_blank" rel="noopener noreferrer" style={{ color: "#0d6efd" }}>
                                            {r.orderNumber}
                                        </a>
                                    ) : (
                                        r.orderNumber
                                    )}
                                </td>
                                {!isADF && <td>{r.name}</td>}
                                {!isADF && <td>{r.phone}</td>}
                                <td>{r.refundAmount.replace(" грн", "")} грн</td>
                                <td>{r.created_at?.slice(0, 10)}</td>
                                {!isManager && <td><CopyOnHover text={r.iban?.replace(/\s+/g, "")} /></td>}
                                {!isManager && <td><CopyOnHover text={r.cardOwnerTaxId} /></td>}
                                {!isManager && <td><CopyOnHover text={r.cardOwner} /></td>}
                                <td>{resolveFopDisplayName(fopOptionItems, userDirectory, r.fop)}</td>
                                {isADF && <td>{renderPaymentAction(r)}</td>}
                                <td style={{ color: "#198754" }}>{r.status}</td>
                                {isManager && <td>
                                    {r.status === "оплачено" && r.paidAt ? formatDateToKyiv(r.paidAt) : ""}
                                </td>
                                }
                                <td><a href={r.driveLink} target="_blank" rel="noopener noreferrer">Відкрити</a></td>
                                <td>
                                    <div className="d-inline-flex gap-1 align-items-center flex-nowrap">
                                        <Button
                                            variant="link"
                                            className="p-0 border-0 shadow-none text-decoration-none lh-1"
                                            style={{ fontSize: "1rem", minWidth: 0, color: "#fd7e14" }}
                                            onClick={() => setEditModalData(r)}
                                            aria-label="Редагувати"
                                        >
                                            ✏️
                                        </Button>
                                        {user?.role === "admin" && (
                                            <Button
                                                variant="link"
                                                className="p-0 border-0 shadow-none text-decoration-none lh-1"
                                                style={{ fontSize: "1rem", minWidth: 0, color: "#dc3545" }}
                                                onClick={() => {
                                                    setDeletingId(r._id);
                                                    setShowConfirmModal(true);
                                                }}
                                                aria-label="Видалити"
                                            >
                                                🗑
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={globalHistoryModalOpen} onHide={() => setGlobalHistoryModalOpen(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Історія змін по заявках</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-body-secondary mb-3">
                        Останні події: редагування, масовий статус, оплата NovaPay, видалення заявок.
                    </p>
                    {recentEventsLoading ? (
                        <div className="small text-body-secondary">Завантаження…</div>
                    ) : recentReturnEvents.length === 0 ? (
                        <div className="small text-body-secondary">Немає записів</div>
                    ) : (
                        <div className="small" style={{ maxHeight: 480, overflowY: "auto" }}>
                            <ul className="list-unstyled mb-0">
                                {recentReturnEvents.map((ev) => (
                                    <li
                                        key={ev._id}
                                        className="py-2 border-bottom border-secondary-subtle"
                                    >
                                        <span className="badge bg-secondary me-2">
                                            {returnEventActionLabel(ev.action)}
                                        </span>
                                        <span className="text-body-secondary">
                                            {formatDateToKyiv(ev.at, "D MMM YYYY HH:mm")}
                                        </span>
                                        {" · "}
                                        <span className="text-body">№{ev.orderNumber || "—"}</span>
                                        {" · "}
                                        <span className="text-body">{ev.actorUsername || "—"}</span>
                                        {ev.summary ? (
                                            <div className="mt-1 text-body">{ev.summary}</div>
                                        ) : null}
                                        {(ev.changes || []).length > 0 ? (
                                            <ul className="mb-0 ps-3 mt-1 text-body-secondary">
                                                {(ev.changes || []).map((c, i) => (
                                                    <li key={i}>
                                                        {c.fieldLabel || c.field}: {c.from} → {c.to}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setGlobalHistoryModalOpen(false)}>
                        Закрити
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!editModalData} onHide={() => setEditModalData(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Редагування заявки</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Номер замовлення</Form.Label>
                        <Form.Control
                            type="text"
                            value={editModalData?.orderNumber || ""}
                            onChange={(e) =>
                                setEditModalData((prev) => ({
                                    ...prev,
                                    orderNumber: e.target.value,
                                }))
                            }
                        />
                    </Form.Group>
                    <p><strong>ПІБ:</strong> {editModalData?.cardOwner}</p>
                    <Form.Group className="mb-3">
                        <Form.Label>Сума повернення</Form.Label>
                        <Form.Control
                            type="text"
                            value={editModalData?.refundAmount || ""}
                            onChange={(e) => setEditModalData((prev) => ({ ...prev, refundAmount: e.target.value }))}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>ФОП</Form.Label>
                        <Form.Select
                            value={
                                findNovapayFopOption(fopModalSelectItems, editModalData?.fop)?.label
                                ?? editModalData?.fop
                                ?? ""
                            }
                            onChange={(e) => setEditModalData((prev) => ({ ...prev, fop: e.target.value }))}
                        >
                            {editModalData?.fop &&
                                !findNovapayFopOption(fopModalSelectItems, editModalData.fop) && (
                                    <option value={editModalData.fop}>{editModalData.fop}</option>
                                )}
                            {fopModalSelectItems.map((option) => (
                                <option key={option.key} value={option.label}>
                                    {option.displayLabel || option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Статус</Form.Label>
                        <Form.Select
                            value={editModalData?.status || "новий"}
                            onChange={(e) => setEditModalData((prev) => ({ ...prev, status: e.target.value }))}
                        >
                            {STATUS_OPTIONS.map((status) => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <div className="mt-3 pt-3 border-top">
                        <Form.Label className="fw-semibold">Історія змін</Form.Label>
                        {editModalHistoryLoading ? (
                            <div className="small text-body-secondary">Завантаження…</div>
                        ) : editModalHistoryEvents.length === 0 ? (
                            <div className="small text-body-secondary">Поки без записів</div>
                        ) : (
                            <div className="small text-body-secondary" style={{ maxHeight: 280, overflowY: "auto" }}>
                                {editModalHistoryEvents.map((h) => (
                                    <div
                                        key={h._id || `${h.at}-${h.action}-${h.actorUsername}`}
                                        className="mb-2 pb-2 border-bottom border-secondary-subtle"
                                    >
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            <span className="badge bg-secondary">
                                                {returnEventActionLabel(h.action)}
                                            </span>
                                            <span>
                                                {formatDateToKyiv(h.at, "D MMM YYYY HH:mm")}
                                                {" · "}
                                                <span className="text-body">{h.actorUsername || "—"}</span>
                                            </span>
                                        </div>
                                        {h.summary ? (
                                            <p className="mb-0 mt-1 text-body">{h.summary}</p>
                                        ) : null}
                                        {(h.changes || []).length > 0 ? (
                                            <ul className="mb-0 ps-3 mt-1">
                                                {(h.changes || []).map((c, i) => (
                                                    <li key={i}>
                                                        {c.fieldLabel || c.field}: {c.from} → {c.to}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEditModalData(null)}>
                        Скасувати
                    </Button>
                    <Button variant="primary" onClick={handleEditSave}>
                        Зберегти
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Підтвердження видалення</Modal.Title>
                </Modal.Header>
                <Modal.Body>Ви дійсно бажаєте видалити цю заяву?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Скасувати
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Видалити
                    </Button>
                </Modal.Footer>
            </Modal>
            {paymentModalData && (
                <NovaPayPaymentModal
                    show={!!paymentModalData}
                    returnData={paymentModalData}
                    fopOptions={fopOptionItems}
                    onHide={() => setPaymentModalData(null)}
                    onSuccess={(id) => {
                        setPaidReturns(prev => ({ ...prev, [id]: true }));
                        // Оновити список — бекенд вже проставив статус «оплачено» + paidAt.
                        fetchReturns();
                        loadRecentReturnEvents();
                    }}
                />
            )}

        </div>
    );
};

export default ReturnsList;
